import React, { useMemo } from 'react';

import { selectBoundingRect } from 'features/settings/selectors';
import { useTypedSelector } from 'lib/centralStore';
import { breakpoint } from 'utility/constant';

/* eslint-disable no-unused-vars */
export enum enComparer {
  less = 1,
  equal = 2,
  greater = 4,
}
/* eslint-enable no-unused-vars */

export interface WindowsWizeCheckProp {
  size: number;
  comparer: enComparer;
}

export interface WindowSizeHookType {
  top: number;
  left: number;
  width: number;
  height: number;
  isMobile: boolean;
  isDesktop: boolean;
  doCheck: (_: Array<WindowsWizeCheckProp>) => Array<boolean>;
}

const useWindowSize = (): WindowSizeHookType => {
  const { height, width, left, top } = useTypedSelector(selectBoundingRect);

  const doCheck = (props: Array<WindowsWizeCheckProp>): Array<boolean> => {
    const isComparer = (input: enComparer, check: enComparer) => (input & check) === check;

    const compare = ({ comparer, size }: WindowsWizeCheckProp): boolean => {
      const wWidth = width ?? breakpoint.sm;
      return Object.values(enComparer)
        .filter((item) => isNaN(Number(item)))
        .some((k) => {
          switch (k) {
            case 'less': {
              return isComparer(comparer, enComparer.less) && wWidth < size;
            }
            case 'equal': {
              return isComparer(comparer, enComparer.equal) && wWidth === size;
            }
            case 'greater': {
              return isComparer(comparer, enComparer.greater) && wWidth > size;
            }
          }
          return false;
        });
    };

    return props.map(compare);
  };

  const { isMobile, isDesktop } = useMemo(() => {
    const wWidth = width ?? breakpoint.sm;
    return {
      isMobile: wWidth < breakpoint.md,
      isDesktop: wWidth >= breakpoint.md,
    };
  }, [width]);

  return {
    top,
    left,
    width,
    height,
    isMobile,
    isDesktop,
    doCheck,
  };
};

export default useWindowSize;